'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { X, MessageCircle, BellOff } from "@/lib/icons";
import { Button } from '@/components/ui/button';
import { useTranslations } from "next-intl"

/** Ticker for a message too long to fit its line. Measures real overflow
 * (not a character-count guess, since font/zoom/viewport all affect what
 * actually fits) and only animates when the text genuinely doesn't fit —
 * a short message just sits still like normal text. Scrolls the full
 * message across, pauses briefly at the start and end so it's readable
 * rather than a blur, then loops. */
function TickerText({ text }: { text: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const span = textRef.current;
    if (!container || !span) return;
    // NOT scrollWidth: on a plain inline element (this span isn't a scroll
    // container, just text) it measured 0 in testing even though the text
    // visibly overflowed by 700+px — offsetWidth is what actually reports
    // an inline element's real rendered width.
    const diff = span.offsetWidth - container.clientWidth;
    setOverflowPx(diff > 0 ? diff : 0);
  }, [text]);

  if (overflowPx <= 0) {
    return (
      <p className="text-sm opacity-90 truncate" ref={containerRef}>
        <span ref={textRef}>{text}</span>
      </p>
    );
  }

  // Duration scales with how far it has to travel so long messages don't
  // whip past — roughly a steady reading speed rather than a fixed time.
  const durationSeconds = Math.max(6, overflowPx / 40);

  return (
    <div className="overflow-hidden text-sm opacity-90" ref={containerRef}>
      <span
        ref={textRef}
        className="inline-block whitespace-nowrap"
        style={{
          animation: `hyper-realism-ticker ${durationSeconds}s ease-in-out infinite`,
          // Exposed as a CSS var so the keyframes below can travel exactly
          // as far as the text actually overflows, not a guessed distance.
          ['--ticker-distance' as string]: `-${overflowPx}px`,
        }}
      >
        {text}
      </span>
    </div>
  );
}

/** Matches the raw shape returned by GET /api/hyper-realism/messages
 * (a `hyper_realism_messages` row + joined `companion`) — kept local rather
 * than reusing the shared camelCase `HyperRealismMessage` type, which
 * doesn't include the companion join or match these column names and was
 * causing this component to reference fields that don't exist on it. */
export interface PresenceBannerMessage {
  id: string;
  companion_id: string;
  content: string;
  media_url: string | null;
  chat_session_id?: string | null;
  companion?: {
    id: string;
    name: string;
    avatar_url: string | null;
  } | null;
}

interface HyperRealismBannerProps {
  message: PresenceBannerMessage;
  onDismiss: (messageId: string) => void;
  onStop: (messageId: string, companionId: string) => void;
}

export function HyperRealismBanner({ message, onDismiss, onStop }: HyperRealismBannerProps) {
  const t = useTranslations("hyperRealismBanner")
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(true);

  const hide = (after: () => void) => {
    setIsVisible(false);
    setTimeout(after, 300); // matches the slide-out animation below
  };

  const handleDismiss = () => hide(() => onDismiss(message.id));
  const handleStop = () => hide(() => onStop(message.id, message.companion_id));

  const handleReply = () => {
    // The message is a real chat message now (see companion-presence-scheduler.ts),
    // so opening the session is a genuine reply, not a dead-end popup.
    router.push(`/goonpals/chat/${message.companion_id}`);
    hide(() => onDismiss(message.id));
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 animate-in slide-in-from-top duration-300"
      style={{
        // The slide-IN itself is already handled by the Tailwind utility
        // classes above (tw-animate-css's animate-in/slide-in-from-top) —
        // this inline `animation` only needs to cover slide-OUT, which has
        // no Tailwind equivalent applied here. Both `slideIn`/`slideOut`
        // keyframe names were referenced with no keyframes defined anywhere
        // in the codebase until the <style> block below — the browser
        // silently no-ops an animation whose keyframes don't exist, so
        // dismissing this banner has never actually animated out, it just
        // sat static for the 300ms timeout then vanished.
        animation: isVisible ? undefined : 'slideOut 0.3s ease-in',
      }}
    >
      <style>{`
        @keyframes slideOut {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(-100%); opacity: 0; }
        }
        @keyframes hyper-realism-ticker {
          0%, 15% { transform: translateX(0); }
          85%, 100% { transform: translateX(var(--ticker-distance)); }
        }
      `}</style>
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            {message.companion?.avatar_url && (
              <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={message.companion.avatar_url}
                  alt={message.companion.name}
                  fill
                  className="object-cover"
                />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {message.companion?.name}
              </p>
              <TickerText text={message.content} />
            </div>

            {message.media_url && (
              <div className="relative w-16 h-16 rounded overflow-hidden flex-shrink-0">
                <Image
                  src={message.media_url}
                  alt={t("messageMedia")}
                  fill
                  className="object-cover"
                />
              </div>
            )}

            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                onClick={handleReply}
                size="sm"
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t("reply")}
              </Button>
              <Button
                onClick={handleStop}
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/20"
                title={t("stopThisCompanionWonTMessageYouOutside")}
              >
                <BellOff className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleDismiss}
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/20"
                title={t("dismiss")}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
